import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import moment from "moment-timezone";
import { omit } from "lodash";
import { toast } from "sonner";
import { useScheduleContext } from "../hooks/context/useScheduleContext";
import { useFilters } from "../../../../utils/hooks/useFilters";
import useApi from "../../../../utils/api";
import { formatTimeSheetParams } from "../../../TimeSheet/api/helpers";
import { formatEvents } from "../helpers/data/formatEvents";
import { formatTemplateEvents } from "../helpers/data/formatTemplateEvents";
import { combineDateAndTime } from "../../../../utils/helpers/date";
import { getErrorMessage } from "../../../../utils/helpers/errors";
import { withLoadingToast } from "../helpers/withLoadingToast";
import { getTransferResults, type TransferResult } from "../components/modals/Transfer/ResponseTable";

export const useTransferMutation = ({
    values,
    close,
    onAfterSuccess,
}: {
    values: any;
    close: () => void;
    onAfterSuccess?: (results: TransferResult[]) => void;
}) => {
    const { t } = useTranslation();
    const { authPost, authGet } = useApi();
    const [filters] = useFilters();
    const {
        resetCount,
        dispatch,
        clearSelectedEvents,
        templates,
        refetchEventStatistics,
        resources,
        allResources,
        calendarEvents,
        moreModalEvents
    } = useScheduleContext();

    const onSuccess = useCallback(async (response: any) => {
        const ids = Array.from(
            new Set([response?.userId, response.newUserId].filter(Boolean))
        );
        onAfterSuccess?.(response?.results || []);
        let from = filters?.from;
        let to = filters?.to;

        if (response.moreModal || filters?.mode === "month") {
            const date = response?.schedule?.date;
            from = moment(date).format("YYYY-MM-DD");
            to = moment(date).format("YYYY-MM-DD");
        }

        const params: any = {
            pageIndex: Number(filters?.page) - 1,
            pageSize: filters?.pageSize,
            ...formatTimeSheetParams({
                filters: filters?.filters,
                from,
                to,
            }),
            sort: filters?.sort
        };

        if (filters?.mode === "month") {
            delete params.pageIndex;
            delete params.pageSize;
        }

        if (response?.isTemplate && filters.mode === "day") {
            params.day = filters.day;
        }

        const res = await Promise.all(ids.map(async (id: string) => {
            const url = response.isTemplate
                ? `/shift-templates/v2/${filters?.template?.id || templates?.[0]?.id}/${id}/events`
                : `/schedules/v2/${id}/events`;
            return authGet(url, { params });
        }));

        if (res?.length) {
            const events = res?.flat();
            if (filters?.mode === "month") {
                const date = moment(response?.schedule?.date).format("YYYY-MM-DD");
                const userMap = new Map<string, any>();
                [...(allResources || []), ...(resources || [])].forEach((resource: any) => {
                    if (resource?.id) userMap.set(resource.id, resource);
                });
                [...(calendarEvents?.[date]?.events || []), ...(moreModalEvents || [])].forEach((event: any) => {
                    if (event?.user?.id) userMap.set(event.user.id, event.user);
                });
                userMap.set(response.user.id, response.user);

                const _events = events?.map((event: any) => {
                    const userId = event?.user?.id ?? event.user;
                    const fullUser = userMap.get(userId) ?? event.user;
                    return { ...event, user: fullUser, resourceId: fullUser?.id ?? event.resourceId };
                });
                const type = response?.moreModal
                    ? "update-more-modal-events-when-transfer"
                    : "update-calendar-events-when-transfer";
                dispatch({
                    type,
                    payload: {
                        date: response?.schedule?.date,
                        events: response?.isTemplate ? formatTemplateEvents(_events, filters.mode) : formatEvents(_events),
                        users: ids
                    }
                });
            } else {
                const _events = (response?.isTemplate && filters.mode === "day")
                    ? events.map((event: any) => ({
                        ...event,
                        startDate: combineDateAndTime(moment(), moment.parseZone(event.startDate)),
                        endDate: combineDateAndTime(moment(), moment.parseZone(event.endDate))
                    }))
                    : events;
                dispatch({
                    type: "update-events-when-transfer",
                    payload: {
                        events: response?.isTemplate ? formatTemplateEvents(_events, filters.mode) : formatEvents(_events),
                        users: ids
                    }
                });
            }
        }

        clearSelectedEvents();
        resetCount?.();
        refetchEventStatistics();

        if (!response?.results?.length) {
            toast.success(
                values?.type === "row"
                    ? t("events-transferred-successfully")
                    : t("event-transferred-successfully")
            );
            close();
        }
    }, [
        filters,
        onAfterSuccess,
        authGet,
        dispatch,
        clearSelectedEvents,
        resetCount,
        refetchEventStatistics,
        close,
        templates,
        values?.type,
        resources,
        allResources,
        calendarEvents,
        moreModalEvents,
        t,
    ]);

    const submitMutation = useMutation({
        ...withLoadingToast(t("transferring")),
        mutationFn: async (data: any) => {
            const url = values?.type === "cell"
                ? (
                    values?.isTemplate
                        ? `/shift-templates/v2/${filters?.template?.id || templates?.[0]?.id}/transfer/${values?.eventId}`
                        : `/schedules/v2/transfer/${values?.eventId}`
                ) : (
                    values?.isTemplate
                        ? `/shift-templates/v2/${filters?.template?.id || templates?.[0]?.id}/transfer/user/${values?.userId}`
                        : `/schedules/v2/transfer/user/${values?.userId}`
                );
            const response = await authPost(url, { data: omit(data, ["user", "isTemplate"]) });
            const results = getTransferResults(response, data?.user);
            return {
                results,
                schedule: results[0]?.schedule,
                user: data?.user,
                newUserId: data.newUserId,
                userId: values?.userId?.id ?? values?.userId,
                moreModal: values?.moreModal,
                isTemplate: values?.isTemplate
            };
        },
        onSuccess,
        onError: (err: any) => getErrorMessage(err, t)
    });

    return { submitMutation };
};
